import { useCallback, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import SolidBedIcon from 'LuxKit/Icons/solid/SolidBedIcon'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import AssistChip from 'LuxKit/Chips/AssistChip'
import Divider from 'LuxKit/Divider'
import noop from 'lib/function/noop'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import useOffer from 'hooks/Offers/useOffer'
import { isBedbankOffer, isLEHotel } from 'lib/offer/offerTypes'
import GooglePlaceRatingDisplay from 'tripPlanner/components/GooglePlaceRating/GooglePlaceRatingDisplay'
import { useGetPlaceInfo } from 'tripPlanner/hooks/api/chat'
import AddToTripDropdown from 'tripPlanner/components/GenericDrawer/AddToTripDropdown'
import AddToTripAction from 'tripPlanner/components/GenericDrawer/AddToTripAction'
import AddHotelToTripButton from 'tripPlanner/components/AddToTrip/AddHotelToTripButton'
import BookedLabel from 'tripPlanner/components/BookedLabel'
import FilterChip from 'LuxKit/Chips/FilterChip'
import { findPlaceStopInItinerary, findNearestStop, isOfferBookedInItinerary, getOfferItineraryDayRange } from 'tripPlanner/utils/chatItinerary'
import type { TripItineraryData } from 'tripPlanner/api/types'
import { mapPlaceImages } from 'tripPlanner/utils/placeImages'
import { getLocalisedDayLabel } from 'tripPlanner/utils/chatDates'
import type { LinkRect } from './types'
import { calculatePreviewPosition, PREVIEW_WIDTH, VIEWPORT_PADDING } from './calculatePreviewPosition'

const imageParams: ImageParams = {
  mobileWidth: '322px',
  tabletWidth: '322px',
  desktopWidth: '322px',
}

const ESTIMATED_PREVIEW_HEIGHT = 350

const PreviewContainer = styled.div`
  position: fixed;
  width: ${rem(PREVIEW_WIDTH)};
  max-width: calc(100vw - ${rem(VIEWPORT_PADDING * 2)});
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border-radius: ${props => props.theme.borderRadius.L};
  box-shadow: ${props => props.theme.shadow.flat.large};
  z-index: 100;
  overflow: hidden;
  cursor: pointer;
  animation: fadeInScale 0.15s ease-out;

  @keyframes fadeInScale {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(${rem(4)});
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`

const ImageCarouselContainer = styled.div`
  position: relative;
  width: 100%;
  height: ${rem(223)};
  flex-shrink: 0;
  overflow: hidden;
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const AddChipContainer = styled.div`
  position: absolute;
  top: ${rem(12)};
  inset-inline-end: ${rem(12)};
  z-index: 10;
`

const BookedOverlayContainer = styled.div`
  position: absolute;
  top: ${rem(12)};
  inset-inline: ${rem(12)};
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const NonInteractiveChipWrapper = styled.div`
  pointer-events: none;
`

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`

interface BasePlaceProps {
  type: 'place'
  placeId: string
  placeName: string
}

interface BaseHotelProps {
  type: 'hotel'
  offerId: string
  offerName: string
}

type Props = (BasePlaceProps | BaseHotelProps) & {
  linkRect: LinkRect
  onClick?: () => void
  onClose?: () => void
  onMouseEnter?: () => void
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string) => void
  hideAddToTrip?: boolean
}

function getOfferLocation(offer: App.AnyOffer): string | undefined {
  if (isLEHotel(offer)) {
    return ('location' in offer && typeof offer.location === 'string') ? offer.location : undefined
  }
  if (isBedbankOffer(offer)) {
    return offer.property?.location ?? undefined
  }
  return undefined
}

function getOfferImages(offer: App.AnyOffer): Array<App.Image> {
  if ('images' in offer && Array.isArray(offer.images) && offer.images.length > 0) {
    return offer.images
  }
  if ('image' in offer && offer.image) {
    return [offer.image]
  }
  return []
}

function HoverPreview(props: Props) {
  const {
    linkRect,
    onClick = noop,
    onClose = noop,
    onMouseEnter: onMouseEnterProp,
    itineraryData,
    onSendMessage,
    hideAddToTrip = false,
  } = props

  const intl = useIntl()
  const toBePlannedLabel = intl.formatMessage({
    id: 'QtMHdz16BAZQ81bJkCXL',
    defaultMessage: 'To be planned',
    description: 'Fallback itinerary day label in the place hover preview when no day has been assigned',
  })
  const isPlace = props.type === 'place'
  const placeId = isPlace ? props.placeId : undefined
  const placeName = isPlace ? props.placeName : undefined
  const offerId = !isPlace ? props.offerId : undefined

  const offerName = !isPlace ? props.offerName : undefined

  // Place data
  const { data: placeInfo, isLoading: placeLoading } = useGetPlaceInfo(placeId)

  // Hotel data
  const [offer, offerLoading] = useOffer<App.AnyOffer>(offerId ?? '')

  // Place: add-to-trip dropdown
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const addChipRef = useRef<HTMLButtonElement>(null)

  const handleAddChipClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setIsDropdownOpen(true)
  }, [])

  const handleExperienceChipContainerClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
  }, [])

  const handleDropdownClose = useCallback(() => {
    setIsDropdownOpen(false)
  }, [])

  const position = useMemo(
    () => calculatePreviewPosition(linkRect, ESTIMATED_PREVIEW_HEIGHT),
    [linkRect],
  )

  const positionStyle = useMemo<React.CSSProperties>(() => ({
    left: position.left,
    ...(position.top !== undefined && { top: position.top }),
    ...(position.bottom !== undefined && { bottom: position.bottom }),
  }), [position])

  // Place itinerary info
  const placeStopInfo = useMemo(
    () => findPlaceStopInItinerary(placeId, itineraryData),
    [placeId, itineraryData],
  )

  const placeCoordinates = useMemo(() => {
    if (!isPlace || placeInfo?.lat == null || placeInfo?.lng == null) return undefined
    return { lat: placeInfo.lat, lng: placeInfo.lng }
  }, [isPlace, placeInfo?.lat, placeInfo?.lng])

  const nearestStop = useMemo(
    () => isPlace ? findNearestStop(placeCoordinates, itineraryData?.stops ?? []) : undefined,
    [isPlace, placeCoordinates, itineraryData?.stops],
  )

  const placeInItinerary = placeStopInfo !== undefined
  const itineraryDayLabel = placeStopInfo
    ? (placeStopInfo.dayNumber !== undefined ? getLocalisedDayLabel(intl, placeStopInfo.dayNumber, placeStopInfo.dayDate) : toBePlannedLabel)
    : undefined

  // Images
  const carouselImages = useMemo((): Array<App.Image> => {
    if (isPlace) {
      if (!placeInfo) return []
      return mapPlaceImages(placeInfo.imageIds, placeInfo.images).slice(0, 4)
    }
    if (!offer) return []
    return getOfferImages(offer)
  }, [isPlace, placeInfo, offer])

  // Loading state
  const isLoading = isPlace ? placeLoading : offerLoading
  const hasData = isPlace ? !!placeInfo : !!offer

  // Hotel derived data
  const propertyName = useMemo(() => {
    if (isPlace) return placeName
    if (!offer) return offerName
    if ('property' in offer && offer.property?.name) return offer.property.name
    return offer.name ?? offerName
  }, [isPlace, placeName, offer, offerName])

  const hotelDescription = useMemo(() => {
    if (isPlace || !offer) return undefined
    if (isLEHotel(offer) && offer.name && offer.name !== propertyName) {
      return offer.name
    }
    return undefined
  }, [isPlace, offer, propertyName])

  const hotelLocation = useMemo(() => {
    if (isPlace || !offer) return undefined
    return getOfferLocation(offer)
  }, [isPlace, offer])

  const hotelCoordinates = useMemo(() => {
    if (isPlace || !offer) return undefined
    const property = 'property' in offer ? offer.property : undefined
    if (property && typeof property === 'object' && 'latitude' in property && 'longitude' in property) {
      const { latitude, longitude } = property as { latitude?: number; longitude?: number }
      if (latitude != null && longitude != null) return { lat: latitude, lng: longitude }
    }
    return undefined
  }, [isPlace, offer])

  // Image carousel rendering
  const imageCarousel = useMemo(() => {
    if (carouselImages.length > 0) {
      return (
        <ImageCarouselContainer>
          <ImageCarousel
            images={carouselImages}
            imageParams={isPlace ? undefined : imageParams}
            eagerLoadFirstImage
          />
        </ImageCarouselContainer>
      )
    }
    if (isLoading) {
      return (
        <ImageCarouselContainer>
          <LoadingBox floating />
        </ImageCarouselContainer>
      )
    }
    return (
      <ImageCarouselContainer>
        <ImagePlaceholder>
          {isPlace
            ? <LineMapMarkerIcon size="L" colour="light-foreground-alt" />
            : <SolidBedIcon size="L" colour="light-foreground-alt" />}
        </ImagePlaceholder>
      </ImageCarouselContainer>
    )
  }, [carouselImages, isLoading, isPlace])

  const addLabel = intl.formatMessage({
    id: 'gv8smPcFaOuLPExDkozL',
    defaultMessage: 'Add',
    description: 'Chip label to add a place to the trip itinerary from the hover preview',
  })

  // Place: add-to-trip chip logic
  const showPlaceAddChip = isPlace && !hideAddToTrip && !placeInItinerary && !!itineraryData && !!onSendMessage
  const showPlaceDayChip = isPlace && !hideAddToTrip && placeInItinerary && !!itineraryDayLabel && !!itineraryData && !!onSendMessage

  const isHotelBooked = !isPlace && isOfferBookedInItinerary(offerId, itineraryData)
  const hotelDayRangeLabel = useMemo(() => {
    if (!isHotelBooked || !offerId) return undefined
    return getOfferItineraryDayRange(offerId, itineraryData)?.label
  }, [isHotelBooked, offerId, itineraryData])

  // Loading skeleton
  if (isLoading && !hasData) {
    return (
      <PreviewContainer
        style={positionStyle}
        onMouseEnter={onMouseEnterProp}
        onMouseLeave={onClose}
        onClick={onClick}
      >
        <ImageCarouselContainer>
          <LoadingBox floating />
        </ImageCarouselContainer>
        <Sheet padding={16}>
          <Group direction="vertical" gap={12}>
            <Group direction="vertical" gap={4}>
              <TextLoadingBox typography="heading6" width="70%" />
              <TextLoadingBox typography="body-small" width="40%" />
            </Group>
            <TextLoadingBox typography="body-medium" width="100%" lines={2} />
          </Group>
        </Sheet>
      </PreviewContainer>
    )
  }

  return (
    <PreviewContainer
      style={positionStyle}
      onMouseEnter={onMouseEnterProp}
      onMouseLeave={onClose}
      onClick={onClick}
    >
      {imageCarousel}

      {/* Place: add-to-trip dropdown chip */}
      {(showPlaceAddChip || showPlaceDayChip) && (
        <AddChipContainer>
          <AssistChip
            ref={addChipRef}
            variant="filled"
            size="medium"
            startIcon={showPlaceAddChip ? <LinePlusIcon /> : undefined}
            endIcon={showPlaceDayChip ? <LineAngleDownIcon /> : undefined}
            onClick={handleAddChipClick}
          >
            {showPlaceDayChip ? itineraryDayLabel : addLabel}
          </AssistChip>
          <AddToTripDropdown
            open={isDropdownOpen}
            onClose={handleDropdownClose}
            triggerRef={addChipRef}
            anchorRef={addChipRef}
            itineraryData={itineraryData}
            placeName={placeName ?? ''}
            onSendMessage={onSendMessage}
            onAfterAction={onClose}
            initialStop={placeStopInfo?.stop ?? nearestStop}
            currentDayNumber={placeStopInfo?.dayNumber}
          />
        </AddChipContainer>
      )}

      {/* Hotel: booked state chips */}
      {isHotelBooked && (
        <BookedOverlayContainer>
          <BookedLabel />
          {hotelDayRangeLabel && (
            <NonInteractiveChipWrapper>
              <FilterChip size="small">
                {hotelDayRangeLabel}
              </FilterChip>
            </NonInteractiveChipWrapper>
          )}
        </BookedOverlayContainer>
      )}
      {!isPlace && !isHotelBooked && !hideAddToTrip && onSendMessage && offer?.type === 'experience' && itineraryData && (
        <AddChipContainer onClick={handleExperienceChipContainerClick}>
          <AddToTripAction
            itineraryData={itineraryData}
            onSendMessage={onSendMessage}
            placeName={propertyName ?? ''}
            offerId={offerId}
            offerType={offer.type}
            coordinates={hotelCoordinates}
            variant="filled"
            onAfterAction={onClose}
          />
        </AddChipContainer>
      )}
      {!isPlace && !isHotelBooked && !hideAddToTrip && onSendMessage && offer?.type !== 'experience' && (
        <AddChipContainer>
          <AddHotelToTripButton
            itineraryData={itineraryData}
            hotelName={propertyName ?? ''}
            offerId={offerId ?? ''}
            offerType={offer?.type}
            hotelCity={hotelLocation}
            hotelCoordinates={hotelCoordinates}
            onSendMessage={onSendMessage}
            size="medium"
            onAfterAction={onClose}
          />
        </AddChipContainer>
      )}

      <Sheet padding={16}>
        <Group direction="vertical" gap={12}>
          {isPlace && (
            <>
              <Group direction="vertical" gap={4}>
                <Heading variant="heading6">{placeName}</Heading>
                {(placeInfo?.rating || placeInfo?.category) && (
                  <Group direction="horizontal" gap={8} verticalAlign="center">
                    {!!placeInfo.rating && (
                      <GooglePlaceRatingDisplay
                        rating={placeInfo.rating}
                        ratingsCount={placeInfo.userRatingCount}
                        size="S"
                      />
                    )}
                    {placeInfo.category && placeInfo.rating && <Divider kind="primary" direction="vertical" />}
                    {placeInfo.category && (
                      <BodyText variant="small" colour="light-foreground-alt">
                        {placeInfo.category}
                      </BodyText>
                    )}
                  </Group>
                )}
              </Group>
              {placeInfo?.description && (
                <BodyText variant="medium" colour="light-foreground-alt" lineClamp={3}>
                  {placeInfo.description}
                </BodyText>
              )}
            </>
          )}

          {!isPlace && (
            <Group direction="vertical" gap={4}>
              <Heading variant="heading6">{propertyName}</Heading>
              {hotelDescription && (
                <BodyText variant="small" colour="light-foreground-alt">
                  {hotelDescription}
                </BodyText>
              )}
              {!hotelDescription && hotelLocation && (
                <Group direction="horizontal" gap={4} verticalAlign="center">
                  <LineMapMarkerIcon size="XS" colour="light-foreground-alt" />
                  <BodyText variant="small" colour="light-foreground-alt">
                    {hotelLocation}
                  </BodyText>
                </Group>
              )}
            </Group>
          )}
        </Group>
      </Sheet>
    </PreviewContainer>
  )
}

export default HoverPreview
